import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeftRight, Database, Zap, Shield, Clock } from "lucide-react";
import Navbar from "../components/Navbar";

const rawMigrations = [
  ["Pinecone", "Qdrant"], ["Pinecone", "Chroma"], ["Pinecone", "Weaviate"],
  ["Pinecone", "Milvus"], ["Pinecone", "MongoDB"], ["Pinecone", "PGVector"],
  ["Milvus", "Qdrant"], ["Milvus", "Chroma"], ["Milvus", "Weaviate"],
  ["Milvus", "MongoDB"], ["Milvus", "PGVector"],
  ["Qdrant", "Chroma"], ["Qdrant", "Weaviate"], ["Qdrant", "Milvus"],
  ["Qdrant", "MongoDB"], ["Qdrant", "PGVector"],
  ["Weaviate", "Chroma"], ["Weaviate", "Qdrant"], ["Weaviate", "Milvus"],
  ["Weaviate", "MongoDB"], ["Weaviate", "PGVector"],
  ["Chroma", "Qdrant"], ["Chroma", "Weaviate"], ["Chroma", "Milvus"],
  ["Chroma", "MongoDB"], ["Chroma", "PGVector"],
  ["MongoDB", "Qdrant"], ["MongoDB", "Chroma"], ["MongoDB", "Weaviate"],
  ["MongoDB", "Milvus"], ["MongoDB", "PGVector"],
  ["PGVector", "Qdrant"], ["PGVector", "Chroma"], ["PGVector", "Weaviate"],
  ["PGVector", "Milvus"], ["PGVector", "MongoDB"],
  ["Weaviate", "Weaviate"], ["Qdrant", "Qdrant"], ["Chroma", "Chroma"],
  ["Milvus", "Milvus"], ["MongoDB", "MongoDB"], ["PGVector", "PGVector"],
];

const processedBySource = (() => {
  const map = {};
  const grouped = {};

  rawMigrations.forEach(([from, to]) => {
    const direct = `${from}->${to}`;
    const reverse = `${to}->${from}`;

    if (from === to) { map[direct] = { from, to, twoWay: true }; return; }
    if (map[reverse]) { map[reverse].twoWay = true; map[direct] = { from, to, twoWay: true }; return; }
    map[direct] = { from, to, twoWay: false };
  });

  Object.values(map).forEach((item) => {
    if (!grouped[item.from]) grouped[item.from] = [];
    grouped[item.from].push(item);
  });

  return grouped;
})();

const databases = Object.keys(processedBySource).sort();

const databaseInfo = {
  Pinecone:  { description: "Managed vector database with high performance",               type: "Managed"     },
  Milvus:    { description: "Open-source vector database for scalable similarity search",  type: "Open Source" },
  Qdrant:    { description: "High-performance vector search engine with rich filtering",   type: "Open Source" },
  Weaviate:  { description: "AI-native vector database with semantic search",              type: "Open Source" },
  Chroma:    { description: "Embedding database built for AI applications",                type: "Open Source" },
  MongoDB:   { description: "Document database with native vector search",                 type: "Managed"     },
  PGVector:  { description: "PostgreSQL extension for vector similarity search",           type: "Extension"   },
};

export default function MigrationFlow() {
  const [selectedDb, setSelectedDb] = useState(databases[0]);

  return (
    <div className="bg-bg min-h-screen">
      <Navbar />

      <section
        id="vector-database-migration-paths"
        className="relative w-full min-h-screen bg-bg py-28 px-4"
        aria-labelledby="migration-paths-heading"
      >
        <div className="w-full max-w-7xl mx-auto space-y-12">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary rounded-xl">
                <Database className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1
                  id="migration-paths-heading"
                  className="text-5xl font-bold text-text-primary"
                >
                  Migration Paths
                </h1>
                <p className="text-text-secondary mt-1">
                  Detailed vector database migration flows
                </p>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              {[
                { icon: <Zap className="w-5 h-5 text-text-primary" />, title: "Fast Migration",  desc: "Optimized transfer speed"  },
                { icon: <Shield className="w-5 h-5 text-text-primary" />, title: "Data Integrity", desc: "Zero data loss guarantee"  },
                { icon: <Clock className="w-5 h-5 text-text-primary" />,  title: "Zero Downtime",  desc: "Seamless transitions"       },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex items-center gap-3 bg-white border border-border rounded-xl p-4">
                  {icon}
                  <div>
                    <div className="font-semibold text-text-primary">{title}</div>
                    <div className="text-sm text-text-secondary">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Database Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            <h2 className="text-2xl font-bold text-text-primary">Select Source Database</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              {databases.map((db) => (
                <button
                  key={db}
                  onClick={() => setSelectedDb(db)}
                  className={`p-4 rounded-xl font-medium transition-all duration-300 text-left border ${
                    selectedDb === db
                      ? "bg-primary text-white border-primary shadow-lg"
                      : "bg-white text-text-primary border-border hover:bg-bg-soft hover:border-border-strong"
                  }`}
                >
                  <div className="font-bold text-lg">{db}</div>
                  <div className={`text-xs mt-1 ${selectedDb === db ? "text-white/70" : "text-text-muted"}`}>
                    {databaseInfo[db].type}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Selected Database Detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedDb}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              {/* Source Database Info */}
              <div className="bg-bg-soft border border-border rounded-2xl p-8">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="p-4 bg-primary rounded-xl">
                        <Database className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-text-primary">{selectedDb}</h3>
                        <p className="text-text-secondary mt-1">{databaseInfo[selectedDb].description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white border border-border rounded-lg">
                    <div className="text-2xl font-bold text-text-primary">
                      {processedBySource[selectedDb].length}
                    </div>
                    <div className="text-xs text-text-muted uppercase tracking-wider">
                      Migration Paths
                    </div>
                  </div>
                </div>
              </div>

              {/* Migration Flows */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-text-primary">Available Migration Paths</h3>
                <div className="space-y-3">
                  {processedBySource[selectedDb].map((item, index) => (
                    <MigrationPath key={index} item={item} index={index} />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Migration Stats Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-primary text-white rounded-2xl p-8 mt-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold">{databases.length}</div>
                <div className="text-white/70 mt-2">Supported Databases</div>
              </div>
              <div>
                <div className="text-4xl font-bold">
                  {Object.values(processedBySource).flat().length}
                </div>
                <div className="text-white/70 mt-2">Total Migration Paths</div>
              </div>
              <div>
                <div className="text-4xl font-bold">
                  {Object.values(processedBySource).flat().filter(m => m.twoWay).length}
                </div>
                <div className="text-white/70 mt-2">Bidirectional Routes</div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
}

/* ----------------------------------
   MIGRATION PATH COMPONENT
----------------------------------- */
function MigrationPath({ item, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSameDb = item.from === item.to;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group"
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-white hover:bg-bg-soft border border-border hover:border-border-strong rounded-xl p-6 transition-all duration-300 shadow-sm hover:shadow-md"
      >
        <div className="flex items-center justify-between">

          {/* Path Flow */}
          <div className="flex items-center gap-6 flex-1">
            {/* Source */}
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-primary rounded-full" />
              <div className="font-bold text-lg text-text-primary">
                {isSameDb ? "Reindex" : item.from}
              </div>
            </div>

            {/* Arrow */}
            <motion.div
              animate={{ x: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="flex-shrink-0"
            >
              {item.twoWay ? (
                <ArrowLeftRight className="w-6 h-6 text-text-muted group-hover:text-text-primary transition-colors" />
              ) : (
                <ArrowRight className="w-6 h-6 text-text-muted group-hover:text-text-primary transition-colors" />
              )}
            </motion.div>

            {/* Destination */}
            <div className="flex items-center gap-3">
              <div className="font-bold text-lg text-text-secondary group-hover:text-text-primary transition-colors">
                {item.to}
              </div>
              <div className="w-3 h-3 bg-text-secondary rounded-full group-hover:bg-primary transition-colors" />
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-3">
            {isSameDb && (
              <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">
                REINDEX
              </span>
            )}
            {item.twoWay && !isSameDb && (
              <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">
                BIDIRECTIONAL
              </span>
            )}
            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
              <ArrowRight className="w-5 h-5 text-text-muted rotate-90" />
            </motion.div>
          </div>
        </div>

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                {[
                  {
                    label: "Migration Type",
                    value: isSameDb ? "Data Reindexing" : item.twoWay ? "Bidirectional Transfer" : "One-way Transfer",
                  },
                  { label: "Data Integrity",  value: "100% Preserved"       },
                  { label: "Estimated Time",  value: "Depends on data volume" },
                ].map(({ label, value }) => (
                  <div key={label} className="space-y-2">
                    <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                      {label}
                    </div>
                    <div className="text-sm text-text-secondary">{value}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}